/**
 * csvMake service - generates CSV from data[[]]
 * Based on https://github.com/asafdav/ng-csv
 */
import { Injectable } from '@angular/core';

const EOL = '\r\n';
const BOM = "\ufeff";

const specialChars = {
  '\\t': '\t',
  '\\b': '\b',
  '\\v': '\v',
  '\\f': '\f',
  '\\r': '\r'
};


@Injectable({
  providedIn: 'root',
})
export class CsvMakeService {
  /**
   * Generates CSV from data[[]] and options.
   */
  public generate(data, options) {
    options.txtDelim = options.txtDelim || '"';
    options.decimalSep = options.decimalSep || '.';
    options.fieldSep = options.fieldSep || ',';
    options.charset = options.charset || 'utf-8';
    options.addByteOrderMarker = true;

    // Replaces any badly formatted special character string with correct special character
    options.fieldSep = this.isSpecialChar(options.fieldSep) ? this.getSpecialChar(options.fieldSep) : options.fieldSep;

    return this.stringify(data, options);
  };

  /**
   * Stringify one field
   */
  private stringifyField(data, options) {
    if (options.decimalSep === 'locale' && this.isFloat(data)) {
      return data.toLocaleString();
    }

    if (options.decimalSep !== '.' && this.isFloat(data)) {
      return data.toString().replace('.', options.decimalSep);
    }

    if (typeof data === 'string') {
      data = data.replace(/"/g, '""'); // Escape double qoutes

      if (options.quoteStrings || data.indexOf(',') > -1 || data.indexOf('\n') > -1 || data.indexOf('\r') > -1) {
        data = options.txtDelim + data + options.txtDelim;
      }

      return data;
    }

    if (typeof data === 'boolean') {
      return data ? 'TRUE' : 'FALSE';
    }

    return data;
  };

  /**
   * Helper function to check if input is float
   */
  private isFloat(input) {
    return +input === input && (!isFinite(input) || Boolean(input % 1));
  };

  /**
   * Creates a csv from a data array
   * @param data
   * @param options
   *  * header - Provide the first row (optional)
   *  * fieldSep - Field separator, default: ',',
   *  * addByteOrderMarker - Add Byte order mark, default(false)
   */
  private stringifyAsync(data, options) {
    // const def = Promise.defer();

    const that = this;
    let csv = "";
    let csvContent = "";



    const dataPromise = Promise.resolve(data).then(responseData => {
      //responseData = angular.copy(responseData);//moved to row creation
      // Check if there's a provided header array
      if (options.header) {
        let encodingArray, headerString;

        encodingArray = [];
        options.header.forEach((title, key) => {
          encodingArray.push(this.stringifyField(title, options));
        });

        headerString = encodingArray.join(options.fieldSep ? options.fieldSep : ",");
        csvContent += headerString + EOL;
      }

      let arrData = [];

      if (Array.isArray(responseData)) {
        arrData = responseData;
      }
      else if (typeof responseData === 'function') {
        arrData = responseData();
      }

      // Check if using keys as labels
      if (options.label && typeof options.label === 'boolean') {
        let labelArray, labelString;

        labelArray = [];
        arrData[0].forEach((value, label) => {
          labelArray.push(this.stringifyField(label, options));
        });
        labelString = labelArray.join(options.fieldSep ? options.fieldSep : ",");
        csvContent += labelString + EOL;
      }

      arrData.forEach((oldRow, index) => {
        const row = [...arrData[index]];
        let dataString, infoArray;

        infoArray = [];

        var iterator = !!options.columnOrder ? options.columnOrder : row;
        iterator.forEach((field, key) => {
          const val = !!options.columnOrder ? row[field] : field;
          infoArray.push(that.stringifyField(val, options));
        });

        dataString = infoArray.join(options.fieldSep ? options.fieldSep : ",");
        csvContent += index < arrData.length ? dataString + EOL : dataString;
      });

      // Add BOM if needed
      if (options.addByteOrderMarker) {
        csv += BOM;
      }

      // Append the content and resolve.
      csv += csvContent;
      return csv;
    });

    return dataPromise;
  };

  /**
   * Creates a csv from a data array
   * @param data
   * @param options
   *  * header - Provide the first row (optional)
   *  * fieldSep - Field separator, default: ',',
   *  * addByteOrderMarker - Add Byte order mark, default(false)
   */
  private stringify(data, options) {
    const that = this;
    let csv = '';
    let csvContent = '';

    // Check if there's a provided header array
    if (options.header) {
      let encodingArray, headerString;

      encodingArray = [];
      options.header.forEach((title, key) => {
        encodingArray.push(that.stringifyField(title, options));
      });

      headerString = encodingArray.join(options.fieldSep ? options.fieldSep : ",");
      csvContent += headerString + EOL;
    }

    const arrData = data;

    // Check if using keys as labels
    if (options.label && typeof options.label === 'boolean') {
      let labelArray, labelString;

      labelArray = [];
      arrData[0].forEach((value, label) => {
        labelArray.push(that.stringifyField(label, options));
      });
      labelString = labelArray.join(options.fieldSep ? options.fieldSep : ",");
      csvContent += labelString + EOL;
    }

    arrData.forEach((oldRow, index) => {
      const row = [...arrData[index]];
      let dataString, infoArray;

      infoArray = [];

      const iterator = !!options.columnOrder ? options.columnOrder : row;
      iterator.forEach((field, key) => {
        const val = !!options.columnOrder ? row[field] : field;
        infoArray.push(that.stringifyField(val, options));
      });

      dataString = infoArray.join(options.fieldSep ? options.fieldSep : ",");
      csvContent += index < arrData.length ? dataString + EOL : dataString;
    });

    // Add BOM if needed
    if (options.addByteOrderMarker) {
      csv += BOM;
    }

    // Append the content
    csv += csvContent;
    return csv;
  };

  /**
   * Helper function to check if input is really a special character
   * @param input
   * @returns {boolean}
   */
  private isSpecialChar(input) {
    return specialChars[input] !== undefined;
  };

  /**
   * Helper function to get what the special character was supposed to be
   * since Angular escapes the first backslash
   * @param input
   * @returns {special character string}
   */
  private getSpecialChar(input) {
    return specialChars[input];
  };

}
